import React, { useState } from 'react';
import { Phone } from 'lucide-react';
import { SUPPORT_DIRECTORY } from '../data/supportDirectory';
import { useAuth } from '../contexts/AuthContext';
import { authFetch } from '../lib/authFetch';

/**
 * Lightweight one-time prompt: shows the first time a signed-in user
 * without a country opens any protected page.  Required so every user's
 * country is captured, which drives the Red / Amber / Get Help screens.
 *
 * - Mounted at the top of DashboardPage.
 * - Dismiss is only possible by picking a country or picking "Skip for now",
 *   which stores a 24-hour snooze in localStorage.
 */
export default function CountryPrompt() {
  const { user, checkAuth } = useAuth();
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const [cc, setCc] = useState('');
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Hide if user already has a country, or they snoozed within the last day.
  const snoozedUntil = (() => {
    try { return parseInt(localStorage.getItem('country_prompt_snooze') || '0', 10); }
    catch (_) { return 0; }
  })();
  const inSnooze = snoozedUntil && Date.now() < snoozedUntil;

  if (!user) return null;
  if (user.country && user.country.length === 2) return null;
  if (inSnooze || dismissed) return null;

  const save = async () => {
    if (!cc) return;
    setBusy(true);
    try {
      await authFetch(`${apiBase}/api/user/country`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ country: cc }),
      });
      try { localStorage.setItem('user_country_code', cc); } catch (_) { /* private mode */ }
      await checkAuth();
      setDismissed(true);
    } catch (_) {
      // Fall through silently; user can try again next visit.
    } finally {
      setBusy(false);
    }
  };

  const snooze = () => {
    try { localStorage.setItem('country_prompt_snooze', String(Date.now() + 24 * 60 * 60 * 1000)); }
    catch (_) { /* private mode */ }
    setDismissed(true);
  };

  return (
    <div
      className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-4"
      data-testid="country-prompt"
    >
      <div className="flex items-start gap-2 mb-2">
        <Phone className="w-4 h-4 text-amber-700 mt-0.5" />
        <div>
          <h3 className="text-sm font-bold text-zinc-900">Pick your country</h3>
          <p className="text-xs text-zinc-700 leading-relaxed">
            We use this to show you the right emergency number and crisis line if you ever need the Red or Amber support screens. You can change it later in your profile.
          </p>
        </div>
      </div>
      <select
        className="w-full border border-amber-300 rounded-md px-2 py-1.5 text-sm bg-white mb-2"
        value={cc}
        onChange={(e) => setCc(e.target.value)}
        data-testid="country-prompt-select"
      >
        <option value="">Choose your country</option>
        {SUPPORT_DIRECTORY.map(c => (
          <option key={c.country_code} value={c.country_code}>{c.country_name}</option>
        ))}
      </select>
      <div className="flex gap-2">
        <button
          onClick={save}
          disabled={!cc || busy}
          className="flex-1 px-3 py-1.5 text-sm font-bold bg-amber-600 hover:bg-amber-700 text-white rounded-md disabled:opacity-50"
          data-testid="country-prompt-save"
        >
          {busy ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={snooze}
          className="px-3 py-1.5 text-sm text-amber-800 hover:text-amber-900 underline"
          data-testid="country-prompt-snooze"
        >
          Not now
        </button>
      </div>
    </div>
  );
}
